import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { useLanguage } from "@/shared/hooks/use-language";
import { Search, Trash2, Key, Plus } from "lucide-react";
import { ScopeContextStorage } from "@/shared/lib/scope-storage.ts";
import { exitToLogin } from "@/shared/lib/session-exit.ts";
import type { ScopeContext } from "@/features/auth";
import { getIntegrations, closeIntegration, getIntegration, type Integration } from "@/features/auth/api/integration.ts";
import { getOwnAccesses } from "@/features/auth/api/user.ts";
import { deriveHolderAccess } from "@/features/auth/model/holder-access.ts";
import type { UserAccessFilters } from "@/features/auth/api/user-access.ts";
import { useAccessListing } from "@/features/auth/model/use-access-listing.ts";
import { AccessCard } from "@/features/auth/ui/AccessCard.tsx";
import { AccessList } from "@/features/auth/ui/AccessList.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { DebugLogger } from "@/shared/lib/debug.ts";
import { useToast } from "@/shared/hooks/use-toast";
import { useDateFormat } from "@/shared/hooks/use-date-format";
import { formatDate } from "@/shared/lib/date-utils";
import { IntegrationCardSkeleton } from "@/shared/components/IntegrationCardSkeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";

// The two tabs answer two questions about one integration: what it is, and what it may
// reach. Management lists the integrations created in the scope; Access lists the
// User-Accesses of type integration the scope has granted -- the same rows the Access
// Management screen shows under its Integrations tab, and deliberately so.
type IntegrationTab = "management" | "access";

// The same filter pair the Access Management screen pins on its Integrations tab, so both
// screens answer "which integrations have access right now" with one query. Module-level
// because the listing refetches whenever this object changes identity.
const INTEGRATION_ACCESS_FILTERS: UserAccessFilters = {
  status: "active",
  user_type: "integration",
};

// Which scope is listed is the point of the toggle above the tabs, and the styled-button
// state was the only thing saying so. Each tab spells it out, and each says what its own
// list holds: integrations created in the scope, against integrations that may reach it.
const TAB_DESCRIPTION_KEY: Record<IntegrationTab, Record<'account' | 'holder', string>> = {
  management: {
    account: "integrations.accountIntegrationsDesc",
    holder: "integrations.holderIntegrationsDesc",
  },
  access: {
    account: "integrations.accountAccessDesc",
    holder: "integrations.holderAccessDesc",
  },
};

// The tab description is what tells the two panels apart, so each panel points at it rather
// than merely sitting next to it.
const TAB_DESCRIPTION_ID = "integrations-tab-description";

const Integrations = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = location.state?.sessionId || ScopeContextStorage.getCurrentSessionId();
  const passedScopeContext = location.state?.scopeContext;
  
  const [accountIntegrations, setAccountIntegrations] = useState<Integration[]>([]);
  const [holderIntegrations, setHolderIntegrations] = useState<Integration[]>([]);
  const [isLoadingAccount, setIsLoadingAccount] = useState(true);
  const [isLoadingHolder, setIsLoadingHolder] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [holderError, setHolderError] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<'account' | 'holder'>('account');
  const [hasHolderAccess, setHasHolderAccess] = useState(false);
  const [activeTab, setActiveTab] = useState<IntegrationTab>('management');
  
  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingIntegration, setDeletingIntegration] = useState<Integration | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Public key modal state
  const [showPublicKeyModal, setShowPublicKeyModal] = useState(false);
  const [publicKeyIntegration, setPublicKeyIntegration] = useState<Integration | null>(null);
  const [publicKey, setPublicKey] = useState<string>("");
  const [isLoadingPublicKey, setIsLoadingPublicKey] = useState(false);
  
  const { toast } = useToast();

  // The access tab lists the same rows the Access Management screen shows for integrations,
  // one listing per scope so the Account / Organization toggle switches between two loaded
  // listings rather than refetching the one being left.
  const accountAccesses = useAccessListing({
    sessionId: sessionId || '',
    filters: INTEGRATION_ACCESS_FILTERS,
  });
  const holderAccesses = useAccessListing({
    sessionId: sessionId || '',
    filters: INTEGRATION_ACCESS_FILTERS,
  });
  // Which scopes the access tab has already asked for. Access is not the landing tab, so
  // nothing is fetched until it is opened; and the holder listing waits on the holder probe
  // the integration listing runs, which can answer after the tab is already open.
  const requestedAccessScopes = useRef({ account: false, holder: false });

  useEffect(() => {
    DebugLogger.debug('Integrations page mounted', { 
      sessionId, 
      passedScopeContext,
      locationState: location.state 
    });
    
    if (!sessionId) {
      DebugLogger.error('No session ID found', { 
        locationState: location.state,
        pathname: location.pathname 
      });
      setError('No session ID found. Please log in again.');
      setIsLoadingAccount(false);
      return;
    }

    // Get scope context from storage or passed state
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId);
    
    if (!contextToUse) {
      DebugLogger.error('No scope context found', { sessionId });
      setError('No scope context found. Please select an account.');
      setIsLoadingAccount(false);
      return;
    }

    // The id is part of "have an account": a stored record carrying an account without one
    // is a scope no request can be made against, and letting it through left the access tab
    // rendering a claim about a listing that was never asked. Rejecting it here gives the
    // same page-level error and escape the other malformed-scope cases get.
    if (!contextToUse.account?.id) {
      DebugLogger.error('Incomplete scope context - no account', { contextToUse });
      setError('Incomplete scope context. Please select an account.');
      setIsLoadingAccount(false);
      return;
    }

    // A new scope invalidates both access listings. Reset before the loader below runs --
    // it is declared after this effect and shares its dependencies, so it re-asks for
    // whichever scope the access tab is showing on the very same change.
    requestedAccessScopes.current = { account: false, holder: false };
    accountAccesses.clear();
    holderAccesses.clear();

    fetchIntegrations(contextToUse, sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, passedScopeContext]);

  // The toggle pair renders behind hasHolderAccess, so losing holder access while the
  // Organization view is open removes the only control that can leave it.
  useEffect(() => {
    if (!hasHolderAccess && viewMode === 'holder') setViewMode('account');
  }, [hasHolderAccess, viewMode]);

  useEffect(() => {
    if (activeTab !== 'access') return;
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
    if (!contextToUse?.account) return;

    // The latch is set by loadAccesses, and only when a request actually goes out. Setting
    // it here instead marked a scope requested that a missing id had silently prevented
    // asking for -- leaving the tab on "No integration has access to this account", a claim
    // about access control from a listing that was never asked, with no skeleton, no error,
    // no retry, and a latch that stopped it ever being re-attempted.
    if (!requestedAccessScopes.current.account) loadAccesses(contextToUse, 'account');

    const holderId = contextToUse.accountHolder?.id || contextToUse.account.holder_id;
    if (hasHolderAccess && holderId && !requestedAccessScopes.current.holder) {
      loadAccesses(contextToUse, 'holder');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, hasHolderAccess, sessionId, passedScopeContext]);

  const loadAccesses = (context: ScopeContext, mode: 'account' | 'holder') => {
    const scopeId = mode === 'account'
      ? context.account?.id
      : context.accountHolder?.id || context.account?.holder_id;
    // No id, no request -- and so no latch either: the scope stays re-attemptable rather
    // than being recorded as asked. This is the only path that can decline to issue.
    if (!scopeId) return;
    requestedAccessScopes.current[mode] = true;
    const listing = mode === 'account' ? accountAccesses : holderAccesses;
    listing.loadFirstPage({ scopeId, scopeType: mode }).catch(() => {
      // The listing already carries the failure, and its own retry is what re-runs this.
    });
  };

  const reloadAccesses = () => {
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
    if (contextToUse) loadAccesses(contextToUse, viewMode);
  };

  // Cancelling an integration closes its user, which is a fact about the access listing as
  // much as about the one being cancelled from. The loader effect cannot carry it -- none
  // of its dependencies move on a cancel -- so the refresh is issued here.
  //
  // Every scope already fetched, not just the one on screen: the toggle switches between
  // two loaded listings, so refreshing only `viewMode` leaves the other one asserting the
  // integration is still active, one click away. Scopes never opened stay unfetched, which
  // is what keeps the access tab free until it is asked for.
  const refreshRequestedAccesses = (context: ScopeContext) => {
    if (requestedAccessScopes.current.account) loadAccesses(context, 'account');
    if (requestedAccessScopes.current.holder) loadAccesses(context, 'holder');
  };

  const refreshIntegrations = () => {
    const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
    if (contextToUse) fetchIntegrations(contextToUse, sessionId || '');
  };

  const fetchIntegrations = async (context: ScopeContext, sessionId: string) => {
    if (!context.account) {
      setError('Account is required to fetch integrations');
      setIsLoadingAccount(false);
      return;
    }

    setError(null);
    setAccountError(null);
    setHolderError(null);

    // Fetch account-scoped integrations first - show these immediately when ready
    try {
      setIsLoadingAccount(true);
      const accountScopeContext: ScopeContext = {
        scopeId: context.account.id,
        scopeType: 'account'
      };
      
      DebugLogger.debug('Fetching account-scoped integrations', { 
        accountId: context.account.id 
      });
      
      const accountIntegrationsResponse = await getIntegrations(sessionId, accountScopeContext);
      setAccountIntegrations(accountIntegrationsResponse.integrations || []);
    } catch (err) {
      DebugLogger.error('Failed to fetch account-scoped integrations', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      setAccountError(err instanceof Error ? err.message : t('integrations.loadAccountFailed'));
      setAccountIntegrations([]);
    } finally {
      setIsLoadingAccount(false);
    }

    // Fetch holder-scoped integrations in the background - don't block UI
    const holderId = context.accountHolder?.id || context.account.holder_id;
    
    if (!holderId) {
      DebugLogger.warn('No holder ID available, skipping holder-scoped integrations', { 
        account: context.account 
      });
      setHolderIntegrations([]);
      setHasHolderAccess(false);
      return;
    }

    loadHolderIntegrations(sessionId, holderId);
  };

  const loadHolderIntegrations = async (sessionId: string, holderId: string) => {
    const holderScopeContext: ScopeContext = { scopeId: holderId, scopeType: 'holder' };
    setIsLoadingHolder(true);
    try {
      let holderAccess: ReturnType<typeof deriveHolderAccess> = 'undetermined';
      try {
        const own = await getOwnAccesses(sessionId, holderScopeContext);
        holderAccess = deriveHolderAccess(own.accesses, Boolean(own.cursor), holderId);
      } catch (err) {
        DebugLogger.warn('Could not resolve own accesses, probing the holder listing instead', {
          error: err instanceof Error ? err.message : 'Unknown error'
        });
      }

      if (holderAccess === 'denied') {
        setHolderIntegrations([]);
        setHasHolderAccess(false);
        return;
      }

      const holderIntegrationsResponse = await getIntegrations(sessionId, holderScopeContext);
      setHolderIntegrations(holderIntegrationsResponse.integrations || []);
      setHasHolderAccess(true);
    } catch (err) {
      const errorCode = (err as { code?: string })?.code;
      setHolderIntegrations([]);
      if (errorCode === 'invalid-permission') {
        DebugLogger.debug('Holder-scoped integrations unavailable for this session');
        setHasHolderAccess(false);
        return;
      }
      DebugLogger.error('Failed to fetch holder-scoped integrations', {
        error: err instanceof Error ? err.message : 'Unknown error'
      });
      setHolderError(err instanceof Error ? err.message : t('integrations.loadHolderFailed'));
      setHasHolderAccess(true);
    } finally {
      setIsLoadingHolder(false);
    }
  };

  const filterIntegrations = (integrations: Integration[]) => {
    if (!searchQuery) return integrations;
    const query = searchQuery.toLowerCase();
    return integrations.filter(integration => 
      integration.id.toLowerCase().includes(query) ||
      integration.user_id.toLowerCase().includes(query) ||
      integration.status.toLowerCase().includes(query)
    );
  };

  const filteredAccountIntegrations = filterIntegrations(accountIntegrations);
  const filteredHolderIntegrations = filterIntegrations(holderIntegrations);
  
  // Get the current list based on view mode
  const currentIntegrations = viewMode === 'account' ? filteredAccountIntegrations : filteredHolderIntegrations;
  const currentError = viewMode === 'account' ? accountError : holderError;
  const currentAccesses = viewMode === 'account' ? accountAccesses : holderAccesses;

  const handleDeleteClick = (integration: Integration) => {
    setDeletingIntegration(integration);
    setDeleteConfirmName("");
    setShowDeleteConfirm(true);
  };

  const handleShowPublicKey = async (integration: Integration) => {
    try {
      setIsLoadingPublicKey(true);
      setPublicKeyIntegration(integration);
      setPublicKey("");
      setShowPublicKeyModal(true);
      
      // Get scope context for the request
      const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
      if (!contextToUse) {
        throw new Error('No scope context available');
      }

      // Use the appropriate scope based on view mode
      let scopeContext: ScopeContext;
      if (viewMode === 'account' && contextToUse.account) {
        scopeContext = {
          scopeId: contextToUse.account.id,
          scopeType: 'account'
        };
      } else if (viewMode === 'holder') {
        const holderId = contextToUse.accountHolder?.id || contextToUse.account?.holder_id;
        if (!holderId) {
          throw new Error('No organization in scope');
        }
        scopeContext = {
          scopeId: holderId,
          scopeType: 'holder'
        };
      } else {
        throw new Error('Invalid scope context for current view mode');
      }

      const response = await getIntegration(sessionId || '', integration.id, scopeContext);
      setPublicKey(response.integration.public_key?.content || "");
    } catch (err) {
      DebugLogger.error('Failed to fetch public key', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      toast({
        title: t("integrations.publicKeyError") || "Error",
        description: err instanceof Error ? err.message : 'Failed to fetch public key',
        variant: "destructive",
      });
      setShowPublicKeyModal(false);
      setPublicKeyIntegration(null);
    } finally {
      setIsLoadingPublicKey(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deletingIntegration) return;
    
    // Validate that the entered name matches
    if (deleteConfirmName !== deletingIntegration.name) {
      toast({
        title: t("integrations.deleteNameMismatch") || "Name mismatch",
        description: t("integrations.deleteNameMismatchDesc") || "The entered name does not match the integration name.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCancelling(true);
      
      // Get scope context for the request
      const contextToUse = passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '');
      if (!contextToUse) {
        throw new Error('No scope context available');
      }

      // Use the appropriate scope based on view mode
      let scopeContext: ScopeContext;
      if (viewMode === 'account' && contextToUse.account) {
        scopeContext = {
          scopeId: contextToUse.account.id,
          scopeType: 'account'
        };
      } else if (viewMode === 'holder') {
        const holderId = contextToUse.accountHolder?.id || contextToUse.account?.holder_id;
        if (!holderId) {
          throw new Error('No organization in scope');
        }
        scopeContext = {
          scopeId: holderId,
          scopeType: 'holder'
        };
      } else {
        throw new Error('Invalid scope context for current view mode');
      }

      await closeIntegration(sessionId || '', deletingIntegration.id, scopeContext);
      
      toast({
        title: t("integrations.deleteSuccess") || "Integration cancelled",
        description: t("integrations.deleteSuccessDesc") || "The integration has been successfully cancelled.",
      });

      // Close modal and refresh list
      setShowDeleteConfirm(false);
      setDeletingIntegration(null);
      setDeleteConfirmName("");
      
      // Both listings, and only on the success path: a refused cancel leaves the access
      // listing correct, so re-asking on its behalf would discard a true list over a write
      // that never landed.
      if (contextToUse) {
        fetchIntegrations(contextToUse, sessionId || '');
        refreshRequestedAccesses(contextToUse);
      }
    } catch (err) {
      DebugLogger.error('Failed to cancel integration', { 
        error: err instanceof Error ? err.message : 'Unknown error' 
      });
      toast({
        title: t("integrations.deleteError") || "Error",
        description: err instanceof Error ? err.message : 'Failed to cancel integration',
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  if (isLoadingAccount) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  {t("integrations.title")}
                </h1>
                <p className="text-muted-foreground">
                  {t("integrations.subtitle")}
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="h-10 w-40 bg-muted rounded animate-pulse" />
              </div>
            </div>

            {/* View mode selector skeleton */}
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <div className="h-10 w-48 bg-muted rounded animate-pulse" />
                <div className="h-10 w-52 bg-muted rounded animate-pulse" />
              </div>
            </div>

            {/* Tab strip + description skeleton */}
            <div className="mb-4">
              <div className="h-10 w-72 bg-muted rounded animate-pulse mb-4" />
              <div className="h-4 w-full max-w-96 bg-muted rounded animate-pulse" />
            </div>

            {/* Search skeleton */}
            <div className="mb-6">
              <div className="h-10 w-full bg-muted rounded animate-pulse" />
            </div>

            {/* Integration cards skeleton */}
            <div className="space-y-4">
              <IntegrationCardSkeleton />
            </div>
          </div>
        </main>
    );
  }

  if (error) {
    return (
      <main className="flex-1 p-4 md:p-8 animate-fadeIn">
          <div className="max-w-7xl mx-auto">
            <Card
              className="bg-destructive/10 border-destructive/20 p-6"
              data-testid="integrations-scope-error"
            >
              <p className="text-destructive">
                <strong>{t("integrations.error") || "Error"}</strong> {error}
              </p>
              <Button onClick={exitToLogin} className="mt-4">
                {t("integrations.signInAgain")}
              </Button>
            </Card>
          </div>
        </main>
    );
  }

  return (
    <>
    <main className="flex-1 p-4 md:p-8 animate-fadeIn">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-foreground mb-2">
                {t("integrations.title")}
              </h1>
              <p className="text-muted-foreground">
                {t("integrations.subtitle")}
              </p>
            </div>
            {activeTab === 'management' && (
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/console/integrations/new", {
                  state: {
                    sessionId,
                    scopeContext: passedScopeContext || ScopeContextStorage.getScopeContext(sessionId || '')
                  }
                })}
                className="bg-primary hover:bg-primary/90 gap-2"
                data-testid="integrations-new"
              >
                <Plus className="w-4 h-4" />
                {t("integrations.newIntegration") || "New Integration"}
              </Button>
            </div>
            )}
          </div>

          {/* The scope selector sits above the tabs because it applies to both: an
              integration and the access it holds are the same thing seen from two sides,
              and reading one scope on one tab and another on the next would not be. */}
          <div className="mb-6">
            {hasHolderAccess && (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={() => setViewMode('account')}
                  variant={viewMode === 'account' ? 'default' : 'outline'}
                  className={viewMode === 'account' ? 'bg-primary text-primary-foreground' : ''}
                  data-testid="integrations-account-toggle"
                >
                  {t("integrations.accountIntegrations")}
                </Button>
                <Button
                  onClick={() => setViewMode('holder')}
                  variant={viewMode === 'holder' ? 'default' : 'outline'}
                  className={viewMode === 'holder' ? 'bg-primary text-primary-foreground' : ''}
                  data-testid="integrations-holder-toggle"
                >
                  {t("integrations.holderIntegrations")}
                </Button>
              </div>
            )}
          </div>

          <Tabs value={activeTab} onValueChange={value => setActiveTab(value as IntegrationTab)}>
            {/* Stopgap, carried from the Access screen: the base TabsList is h-10
                inline-flex with no wrap, so long triggers clip at 375px in pt. The fix
                belongs in the shared component, with both screens checked together. */}
            <TabsList className="mb-4 flex-wrap h-auto">
              <TabsTrigger value="management" data-testid="integrations-tab-management">
                {t("integrations.tabManagement")}
              </TabsTrigger>
              <TabsTrigger value="access" data-testid="integrations-tab-access">
                {t("integrations.tabAccess")}
              </TabsTrigger>
            </TabsList>

            <p
              id={TAB_DESCRIPTION_ID}
              className="text-sm text-muted-foreground mb-4"
              data-testid="integrations-tab-description"
            >
              {t(TAB_DESCRIPTION_KEY[activeTab][viewMode])}
            </p>

            <TabsContent value="management" aria-describedby={TAB_DESCRIPTION_ID}>
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    aria-label={t("integrations.searchPlaceholder")}
                    placeholder={t("integrations.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-card border-border"
                    data-testid="integrations-search"
                  />
                </div>
              </div>

              <div className="mb-8">
                {(viewMode === 'account' && isLoadingAccount) || (viewMode === 'holder' && isLoadingHolder) ? (
                  <div className="space-y-4">
                    <IntegrationCardSkeleton />
                  </div>
                ) : currentError ? (
                  <Card className="bg-card border-border p-6">
                    <div className="py-8">
                      <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                        <p className="text-sm text-destructive mb-3" data-testid="integrations-error">
                          {currentError}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={refreshIntegrations}
                          data-testid="integrations-retry"
                        >
                          {t("integrations.retry")}
                        </Button>
                      </div>
                    </div>
                  </Card>
                ) : currentIntegrations.length === 0 ? (
                  <Card className="bg-card border-border p-6">
                    <div className="text-center py-8">
                      <p className="text-muted-foreground">
                        {viewMode === 'account'
                          ? (t("integrations.noAccountIntegrations") || "No integrations found for this account")
                          : (t("integrations.noHolderIntegrations") || "No integrations found for this organization")
                        }
                      </p>
                    </div>
                  </Card>
                ) : (
                  <div className="space-y-4 animate-fadeIn">
                    {currentIntegrations.map((integration) => (
                      <IntegrationCard 
                        key={integration.id} 
                        integration={integration}
                        onDelete={handleDeleteClick}
                        onShowPublicKey={handleShowPublicKey}
                      />
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="access" aria-describedby={TAB_DESCRIPTION_ID}>
              <Card className="bg-card border-border p-4 mb-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {t("integrations.accessReadOnlyNote")}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    // Carries the tab and the scope, so the control lands on the rows it
                    // was offered next to rather than on the Access screen's own defaults
                    // (Persons, Account). The destination falls back to those defaults, so
                    // a direct visit to /console/access is unaffected.
                    onClick={() => navigate("/console/access", {
                      state: {
                        sessionId,
                        scopeContext: passedScopeContext
                          || ScopeContextStorage.getScopeContext(sessionId || ''),
                        accessTab: "integrations",
                        accessViewMode: viewMode,
                      }
                    })}
                    data-testid="integrations-go-to-access"
                  >
                    {t("integrations.goToAccess")}
                  </Button>
                </div>
              </Card>

              <AccessList
                state={currentAccesses.state}
                emptyMessage={t(viewMode === 'account'
                  ? "integrations.noAccountAccess"
                  : "integrations.noHolderAccess")}
                searchPlaceholder={t("integrations.searchPlaceholder")}
                onRetry={reloadAccesses}
                onLoadMore={currentAccesses.loadMore}
                onRetryLoadMore={currentAccesses.retryLoadMore}
                onContinue={currentAccesses.continuePastCap}
                renderCard={access => <AccessCard key={access.id} access={access} />}
              />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("integrations.deleteConfirmTitle") || "Cancel Integration"}</DialogTitle>
            <DialogDescription>
              {t("integrations.deleteConfirmDesc") || "Are you sure you want to cancel this integration? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              {t("integrations.deleteConfirmNamePrompt") || "To confirm, please type the integration name:"}
            </p>
            <p className="text-sm font-semibold text-foreground mb-2">
              {deletingIntegration?.name}
            </p>
            <Input
              placeholder={t("integrations.deleteConfirmNamePlaceholder") || "Enter integration name"}
              value={deleteConfirmName}
              onChange={(e) => setDeleteConfirmName(e.target.value)}
              className="bg-background border-border"
              data-testid="integrations-delete-name"
            />
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteConfirm(false);
                setDeletingIntegration(null);
                setDeleteConfirmName("");
              }}
              disabled={isCancelling}
              data-testid="integrations-delete-dismiss"
            >
              {t("integrations.cancel") || "Cancel"}
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isCancelling || deleteConfirmName !== deletingIntegration?.name}
              data-testid="integrations-delete-confirm"
            >
              {isCancelling 
                ? (t("integrations.cancelling") || "Cancelling...")
                : (t("integrations.confirm") || "Confirm")
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Public Key Dialog */}
      <Dialog open={showPublicKeyModal} onOpenChange={setShowPublicKeyModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("integrations.publicKeyTitle") || "Public Key"}</DialogTitle>
            <DialogDescription>
              {t("integrations.publicKeyDesc") || "Verify that the public key information matches the one you have on your side."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {isLoadingPublicKey ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">{t("integrations.loadingPublicKey") || "Loading public key..."}</p>
              </div>
            ) : publicKey ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t("integrations.integrationName") || "Integration"}:
                  </p>
                  <p className="text-sm font-semibold text-foreground">
                    {publicKeyIntegration?.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-2">
                    {t("integrations.publicKeyLabel") || "Public Key"}:
                  </p>
                  <div className="bg-muted rounded-md p-4 border border-border">
                    <pre className="text-xs font-mono text-foreground whitespace-pre-wrap break-all">
                      {publicKey}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">{t("integrations.noPublicKey") || "No public key available"}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button 
              onClick={() => {
                setShowPublicKeyModal(false);
                setPublicKeyIntegration(null);
                setPublicKey("");
              }}
            >
              {t("integrations.close") || "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface IntegrationCardProps {
  integration: Integration;
  onDelete: (integration: Integration) => void;
  onShowPublicKey: (integration: Integration) => void;
}

const INTEGRATION_STATUS_KEY: Record<string, string> = {
  created: "access.statusCreated",
  active: "integrations.active",
  blocked: "access.statusBlocked",
  frozen: "access.statusFrozen",
  closed: "access.statusClosed",
};

const INTEGRATION_STATUS_STYLE: Record<string, string> = {
  active: "bg-green-500/10 text-green-500",
  created: "bg-yellow-500/10 text-yellow-500",
  blocked: "bg-yellow-500/10 text-yellow-500",
  frozen: "bg-yellow-500/10 text-yellow-500",
  closed: "bg-muted text-muted-foreground",
};

const IntegrationCard = ({ integration, onDelete, onShowPublicKey }: IntegrationCardProps) => {
  const { t } = useLanguage();
  const { dateFormat } = useDateFormat();

  return (
    <Card className="bg-card border-border p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-xl font-semibold text-foreground">
              {integration.name}
            </h3>
            <Badge
              variant={integration.status === "active" ? "default" : "secondary"}
              className={
                INTEGRATION_STATUS_STYLE[integration.status] || "bg-muted text-muted-foreground"
              }
            >
              {t(INTEGRATION_STATUS_KEY[integration.status] || "integrations.inactive")}
            </Badge>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4 break-all">
            {t("integrations.id") || "ID"}: {integration.id}
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8 mb-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">
                {t("integrations.created") || "Created"}
              </p>
              <p className="text-sm font-medium text-foreground">
                {formatDate(integration.created, dateFormat)}
              </p>
            </div>

            {integration.allowed_ips && integration.allowed_ips.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1">
                  {t("integrations.allowedIPs") || "Allowed IPs"}
                </p>
                <p className="text-sm font-medium text-foreground">
                  {integration.allowed_ips.join(", ")}
                </p>
              </div>
            )}
          </div>

        </div>

        <div className="flex gap-2 ml-4">
          <Button
            variant="ghost"
            size="sm"
            className="text-primary hover:text-primary/90 hover:bg-primary/10"
            onClick={() => onShowPublicKey(integration)}
          >
            <Key className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-destructive hover:text-destructive/90 hover:bg-destructive/10"
            onClick={() => onDelete(integration)}
            data-testid="integrations-delete"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default Integrations;

